import { useState, useCallback, useEffect } from 'react';
import { UseFileManagerReturn, FileListQuery, FileInfo } from '../../../types/certificate';
import { getFileList, deleteFile as deleteFileService, incrementDownloadCount } from '../../../services/fileService';

/**
 * 文件管理自定义Hook
 * 管理文件列表、搜索、分页、删除等功能
 */
export const useFileManager = (): UseFileManagerReturn => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  
  // 初始查询参数
  const [query, setQuery] = useState<FileListQuery>({
    type: 'all',
    page: 1,
    pageSize: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // 加载文件列表
  const loadFiles = useCallback(async (searchQuery?: FileListQuery) => {
    setLoading(true);
    setError(null);
    
    try {
      const finalQuery = { ...query, ...searchQuery };
      const response = await getFileList(finalQuery);
      
      setFiles(response.files);
      setTotal(response.total);
      setCurrentPage(response.page);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载文件列表失败');
      console.error('加载文件列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, [query]);

  // 更新查询参数
  const updateQuery = useCallback((newQuery: Partial<FileListQuery>) => {
    const updatedQuery = { ...query, ...newQuery };
    
    // 如果更改了筛选条件，重置到第一页
    if (newQuery.type !== undefined || 
        newQuery.orderNumber !== undefined || 
        newQuery.startDate !== undefined || 
        newQuery.endDate !== undefined) {
      updatedQuery.page = 1;
    }
    
    setQuery(updatedQuery);
  }, [query]);

  // 刷新文件列表
  const refreshFiles = useCallback(async () => {
    await loadFiles();
  }, [loadFiles]);

  // 删除文件
  const deleteFile = useCallback(async (fileId: string) => {
    try {
      await deleteFileService(fileId);
      
      // 删除成功后刷新列表
      await refreshFiles();
      
      // 如果当前页没有文件了，回到上一页
      if (files.length === 1 && currentPage > 1) {
        updateQuery({ page: currentPage - 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除文件失败');
      throw err;
    }
  }, [files.length, currentPage, refreshFiles, updateQuery]);

  // 下载文件
  const downloadFile = useCallback(async (file: FileInfo) => {
    try {
      // 更新下载计数
      await incrementDownloadCount(file.id);
      
      // 创建下载
      const response = await fetch(file.url);
      if (!response.ok) {
        throw new Error(`文件不存在或服务器错误: ${response.status}`);
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.filename || `${file.orderNumber}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (error) {
      console.error('下载文件失败:', error);
      throw new Error(`下载失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }, []);

  // 组件挂载时加载数据
  useEffect(() => {
    loadFiles();
  }, [query]); // 当查询参数变化时重新加载

  return {
    files,
    loading,
    error,
    query,
    total,
    currentPage,
    totalPages,
    updateQuery,
    refreshFiles,
    deleteFile,
    downloadFile
  };
};